import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as turf from '@turf/turf';
import * as mapboxgl from 'mapbox-gl';

import { Site } from '../../shared/models';
import { MapControlState } from '../../store/map-control/map-control.reducer';

@Injectable({
  providedIn: 'root',
})
export class MapService {
  currentMap = null;
  // Toggle Roads
  layers = [
    'road-street-case',
    'road-street-low',
    'road-minor-case',
    'road-minor-low',
    'road-secondary-tertiary-case',
    'road-motorway-trunk-case',
    'road-major-link-case',
    'road-primary-case',
    'road-major-link',
    'road-minor',
    'road-street',
    'road-secondary-tertiary',
    'road-primary',
    'road-motorway-trunk',
    'road-label',
    'road-label-outdoors',
    'road-number-shield',
    'road-exit-shield',
  ];
  gridFillImage: HTMLImageElement;
  gridWhiteImage: HTMLImageElement;

  loadImages() {
    // Load an image to use as the pattern from an external URL.
    this.currentMap?.loadImage('../../../../../assets/blank-white.png', (err, image) => {
      if (err) {
        throw err;
      }
      this.gridWhiteImage = image;
      this.currentMap.addImage('blank-white', image);
    });
    this.currentMap?.loadImage('../../../../../assets/grid-fill.png', (err, image) => {
      if (err) {
        throw err;
      }
      this.gridFillImage = image;
      this.currentMap.addImage('grid-fill', image);
    });
  }

  reloadImages() {
    this.currentMap.on('styledata', () => {
      if (!this.currentMap.hasImage('blank-white')) {
        this.currentMap.addImage('blank-white', this.gridWhiteImage);
      }
      if (!this.currentMap.hasImage('grid-fill')) {
        this.currentMap.addImage('grid-fill', this.gridFillImage);
      }
    });
  }

  setRoadLayerVisibility(map: mapboxgl.Map, mapControls: MapControlState) {
    this.currentMap?.getStyle().layers.forEach(layer => {
      if (layer["source-layer"] == 'road') {
        map.setLayoutProperty(layer.id, 'visibility', mapControls.displayRoads ? 'visible' : 'none');
      }
    })
  }

  flyToBoundingBox(feature: any) {
    if (this.currentMap) {
      const boundingBox = turf.bbox({
        type: 'FeatureCollection',
        features: [feature],
      }) as any;
      this.currentMap.fitBounds(boundingBox, {
        padding: 200,
        animate: true,
      });
    }
  }

  navigateToMap(router: Router, site: Site, prefix: string) {
    if (site.versions[0].parcels.length > 0) {
      router.navigateByUrl(`/${prefix}/${site.id}/map-site`);
    } else {
      router.navigateByUrl(`/${prefix}/${site.id}/locate-property/select-parcels`);
    }
  }

  toggleLayer(layerId: string, propertyName: string, propertyValue: string) {
    if (this.currentMap?.getLayer(layerId) !== undefined) {
      this.currentMap.setLayoutProperty(layerId, propertyName, propertyValue);
    }
  }

  setLayerVisibility(currentMapControls: MapControlState) {
    this.toggleLayer('kml-import', 'visibility', currentMapControls.displayKMLUpload ? 'visible' : 'none');
    this.toggleLayer('kml-import-outline', 'visibility', currentMapControls.displayKMLUpload ? 'visible' : 'none');
    this.toggleLayer('waterways-data1', 'visibility', currentMapControls.displayWaterways ? 'visible' : 'none');
    this.toggleLayer('waterways-data2', 'visibility', currentMapControls.displayWaterways ? 'visible' : 'none');
    this.toggleLayer('forest-data', 'visibility', currentMapControls.displayForest ? 'visible' : 'none');
    this.toggleLayer('forest-data-outline', 'visibility', currentMapControls.suggestedStandsSelector ? 'visible' : 'none');
    this.toggleLayer('public-land-data', 'visibility', currentMapControls.displayPublicLand ? 'visible' : 'none');

    if (this.currentMap?.getLayer('forest-data')) {
      if (currentMapControls.suggestedStandsSelector) {
        this.currentMap.setPaintProperty('forest-data', 'fill-outline-color', 'rgba(255,255,255,0.5)');
        this.currentMap.setPaintProperty('forest-data-outline', 'line-color', 'rgba(255,255,255,0.5)');
      } else {
        this.currentMap.setPaintProperty('forest-data', 'fill-outline-color', 'rgba(72,210,7,0.0)');
        this.currentMap.setPaintProperty('forest-data-outline', 'line-color', 'rgba(72,210,7,0.0)');
      }
    }

    this.setRoadLayerVisibility(this.currentMap, currentMapControls);
  }
}
